import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Check, Eye, Clock, Ban, FileWarning, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { documentCaptureService } from '../../services/documentCaptureService';
import { useReviewDecision } from '../../hooks/useReviewDecision';
import { DocReviewReason, type NsPreview, type ReviewQueueItem } from '../../types/documentCapture.types';
import { IdentificationPanel } from './identification/IdentificationPanel';
import { isPrefixNotHandled } from './identification/CorroborationFacts';

/**
 * Scans-tab action form (86d3rrfdk) — the flagship assign flow.
 *
 * Type a number → the NS preview card fades in (record · entity · amount · date) → the reviewer
 * eyeball-matches it against the visible scan → Confirm & link. Confirm is disabled until the number
 * looks complete AND a live NS match has been previewed — blind entry is how documents get mislinked.
 *
 * "Confirm & link" dispatches to TWO endpoints (both look identical to the reviewer):
 *  - an unidentified scan (no document) → `assign` (no epoch, the file has no document yet)
 *  - a multi-match item (has a document + candidates) → handled by the NetSuite tab's picker, not here
 * This panel is the unidentified-scan path; the multi-match picker lives in NsReviewPanel.
 */

// Matches the server CapitolShape + the assign validator. Client gate is "looks complete" only.
const CAPITOL_SHAPE = /^[A-Z]{2,4}-\d{6,}$/;
const PREVIEW_DEBOUNCE_MS = 400;

interface ScansReviewPanelProps {
  item: ReviewQueueItem;
}

export function ScansReviewPanel({ item }: ScansReviewPanelProps) {
  const { submit, submitting } = useReviewDecision();

  const [docNumber, setDocNumber] = useState('');
  const [preview, setPreview] = useState<NsPreview | null>(null);
  // `notHandled` is split out from `error` (86d3z2nzb): a 404 here means the prefix is unregistered
  // or out of the reviewer's scope, which is an answer, not an outage — and telling them to retry is
  // advice that can never work.
  const [previewState, setPreviewState] = useState<
    'idle' | 'loading' | 'done' | 'notHandled' | 'error'
  >('idle');
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [openingPdf, setOpeningPdf] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openPdf = useCallback(async () => {
    setOpeningPdf(true);
    try {
      await documentCaptureService.openReviewItemPdf(item.id);
    } catch {
      toast.error('Couldn’t open the PDF — try again in a moment.');
    } finally {
      setOpeningPdf(false);
    }
  }, [item.id]);

  const normalized = docNumber.trim().toUpperCase();
  const shapeOk = CAPITOL_SHAPE.test(normalized);
  const singleMatch = preview?.count === 1 && !preview.matches[0].voided;

  // 86d3x48q7 T13 — the label morphs where a machine proposed a number, because the reviewer's job
  // changes: with a suggestion on screen they are CHECKING a value rather than supplying one, and
  // the instruction has to say which.
  const numberLabel =
    item.reason === DocReviewReason.AiSuggestion
      ? 'Number to link — check it against the scan'
      : item.reason === DocReviewReason.IdentificationConflict
        ? 'Number to link'
        : 'Document number';

  // Live NS preview, debounced — never fire per keystroke.
  useEffect(() => {
    setPreview(null);
    setPreviewState('idle');
    if (!shapeOk) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewState('loading');
      try {
        const result = await documentCaptureService.previewNetSuite(normalized);
        setPreview(result);
        setPreviewState('done');
      } catch (err) {
        setPreviewState(isPrefixNotHandled(err) ? 'notHandled' : 'error');
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [normalized, shapeOk]);

  const confirmLink = useCallback(() => {
    void submit(() => documentCaptureService.assign(item.id, normalized), {
      successMessage: `${normalized} assigned and queued to link`,
      // 86d3x48q7 T13 — walk this scan's stream row through the linking step. A person just did what
      // the readers could not, and the feed should show that rather than a row that quietly turns
      // green: the live journey is where operators learn how identification actually works here.
      settle: { fileId: item.fileId, docNumber: normalized },
    });
  }, [submit, item.id, item.fileId, normalized]);

  const rejectScan = useCallback(() => {
    if (!reason.trim()) return;
    // An unattached scan has no epoch; the server ignores it on assign/reject-of-unattached, but the
    // reject DTO requires the field — send 0.
    void submit(() => documentCaptureService.reject(item.id, reason.trim(), item.linkEpoch ?? 0), {
      successMessage: 'Scan rejected',
    });
  }, [submit, item.id, reason, item.linkEpoch]);

  return (
    // NOTE: deliberately a <div>, not a <form>. Enter must not confirm — see IdentificationPanel's
    // anti-reflexive contract. A form wrapper would silently bind Enter to the first submit button
    // and hand back the one-keystroke approval the whole design is built to prevent.
    <div className="space-y-4">
      {/* 86d3x48q7 T13 — what the two readers made of this scan, above the field they feed. */}
      <IdentificationPanel item={item} onAdopt={setDocNumber} current={docNumber} />

      <div>
        <label htmlFor="assign-number" className="mb-1 block text-sm font-medium text-foreground">
          {numberLabel}
        </label>
        <Input
          id="assign-number"
          value={docNumber}
          onChange={(e) => setDocNumber(e.target.value)}
          placeholder="e.g. DR-000044555"
          autoComplete="off"
          className="h-9 font-mono"
          aria-describedby="assign-hint"
        />
        {/* 86d3z2nze — no directional language. The panes stack below lg, so "on the left" was wrong
            at two of the three breakpoint bands. */}
        <p id="assign-hint" className="mt-1 text-xs text-muted-foreground">
          Read it off the scan. DOP checks NetSuite before linking.
        </p>
      </div>

      {/*
        86d3w2jrj FE-1 — an unreadable scan lands in this tab now, and it is the one case where the
        pane on the left may be empty: page images are rendered DURING a decode, so a decode that
        failed or timed out produced none. Say so, and offer the PDF, rather than leaving the
        reviewer staring at "Scan image unavailable" beside an instruction to read a number off it.
      */}
      {item.reason === DocReviewReason.DecodeFailed && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
          <div className="flex items-start gap-2">
            <FileWarning className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
            <div className="min-w-0 space-y-2 text-sm">
              <p className="text-foreground">
                DOP couldn’t read a barcode on this scan, so the preview may be blank — page images
                are produced during a decode, and this one never finished. Open the original PDF to
                read the number off the page.
              </p>
              <Button variant="outline" size="sm" onClick={openPdf} disabled={openingPdf}>
                {openingPdf ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <FileText className="mr-1.5 h-4 w-4" aria-hidden />
                )}
                Open the PDF
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* NS preview card — the eyeball-match evidence, before commit. */}
      {shapeOk && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          {/* Skeleton, not a spinner: this is a card whose CONTENT is loading, and it stands where
              the answer will appear. Spinners stay for button-busy states only. */}
          {previewState === 'loading' && (
            <div className="space-y-1.5">
              <span className="sr-only">Checking NetSuite for this number</span>
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-3 w-24" />
            </div>
          )}
          {previewState === 'notHandled' && (
            <p className="text-sm text-muted-foreground">
              DOP can’t look this number up — its series isn’t set up here, or it’s outside what you
              can see.
            </p>
          )}
          {previewState === 'error' && (
            <p className="text-sm text-destructive">Couldn’t reach NetSuite — try again in a moment.</p>
          )}
          {previewState === 'done' && preview && preview.count === 0 && (
            <p className="text-sm text-warning">No NetSuite transaction has this number. Check the digits.</p>
          )}
          {previewState === 'done' && preview && preview.count > 1 && (
            <p className="text-sm text-warning">
              {preview.count} transactions share this number — this can’t be linked from here.
            </p>
          )}
          {previewState === 'done' && singleMatch && preview && (
            <div className="flex items-start gap-2">
              <Eye className="mt-0.5 h-4 w-4 flex-shrink-0 text-info" aria-hidden />
              <div className="min-w-0 text-sm">
                <div className="font-medium text-foreground">{preview.nsRecordType}</div>
                <div className="text-muted-foreground">{preview.matches[0].entityName ?? '—'}</div>
                <div className="tabular-nums text-muted-foreground">
                  {preview.matches[0].tranDate ?? '—'}
                  {preview.matches[0].amount != null &&
                    ` · ${new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(
                      preview.matches[0].amount
                    )}`}
                </div>
              </div>
            </div>
          )}
          {previewState === 'done' && preview?.count === 1 && preview.matches[0].voided && (
            <p className="text-sm text-warning">That NetSuite transaction is voided — it can’t be linked.</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {/* 86d3x48q7 T13 — the button NAMES the number once one is in the field. Naming it forces the
            reviewer's eye onto the exact value they are committing, which is the whole defence against
            a one-character misread (…626 vs …628) sailing through on a reflexive click. */}
        <Button onClick={confirmLink} disabled={!singleMatch || submitting} size="sm">
          {submitting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden /> : <Check className="mr-1.5 h-4 w-4" aria-hidden />}
          {shapeOk ? `Confirm ${normalized} & link` : 'Confirm & link'}
        </Button>
        {/* Keep watching only applies once the item has a document (NeverAppearedInNs); an
            unidentified scan has nothing to watch yet, so it's shown only where it's valid. */}
        {item.reason === DocReviewReason.NeverAppearedInNs && item.linkEpoch != null && (
          <Button
            variant="outline"
            size="sm"
            disabled={submitting}
            onClick={() =>
              void submit(() => documentCaptureService.keepWatching(item.id, item.linkEpoch ?? 0), {
                successMessage: 'Watching for this number in NetSuite',
              })
            }
          >
            <Clock className="mr-1.5 h-4 w-4" aria-hidden />
            Keep watching
          </Button>
        )}
        <Button variant="ghost" size="sm" disabled={submitting} onClick={() => setRejecting((v) => !v)}>
          <Ban className="mr-1.5 h-4 w-4" aria-hidden />
          Reject scan
        </Button>
      </div>

      {rejecting && (
        <div className="space-y-2 rounded-lg border border-destructive/25 bg-destructive/5 p-3">
          <label htmlFor="reject-reason" className="block text-sm font-medium text-foreground">
            Why is this being rejected? <span className="text-destructive">*</span>
          </label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Not a Capitol document, or a mis-scan."
            rows={2}
          />
          <Button
            variant="destructive"
            size="sm"
            disabled={!reason.trim() || submitting}
            onClick={rejectScan}
          >
            Reject scan
          </Button>
        </div>
      )}
    </div>
  );
}
